import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import moment from "moment";

import { axios, getClientUrl, getNumericalHeader } from "../utils";

const MIN_EXPORT_START_DATE = "2020-01-01";

function daysInclusive(startYmd, endYmd) {
  if (!startYmd || !endYmd) return null;
  const a = moment(startYmd, "YYYY-MM-DD", true);
  const b = moment(endYmd, "YYYY-MM-DD", true);
  if (!a.isValid() || !b.isValid()) return null;
  return b.diff(a, "days") + 1;
}

function boolOrNull(v) {
  if (v === "true") return true;
  if (v === "false") return false;
  return null;
}

export default function ExportRequestsPanel({
  domain,
  category,
  defaultStartDate,
  defaultEndDate,
}) {
  const [startDate, setStartDate] = useState(defaultStartDate || "");
  const [endDate, setEndDate] = useState(defaultEndDate || "");
  const [email, setEmail] = useState("");

  const [isStorewide, setIsStorewide] = useState("none");
  const [isSignificant, setIsSignificant] = useState("none");
  const [isSteep, setIsSteep] = useState("none");
  const [isClearance, setIsClearance] = useState("none");
  const [isDesperation, setIsDesperation] = useState("none");

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [requestId, setRequestId] = useState(null);
  const [requestDetail, setRequestDetail] = useState(null);
  const [pollError, setPollError] = useState("");

  const pollTimerRef = useRef(null);

  const days = useMemo(() => daysInclusive(startDate, endDate), [startDate, endDate]);
  const isAsyncEligible = days !== null && days > 30;

  const stopPoll = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchDetail = async (id) => {
    const url = getClientUrl(`v1/export-requests/${id}/`);
    const res = await axios.get(url, getNumericalHeader());
    return res.data;
  };

  useEffect(() => {
    return () => stopPoll();
  }, []);

  useEffect(() => {
    if (!requestId) return;

    let cancelled = false;
    setPollError("");

    const tick = async () => {
      try {
        const r = await fetchDetail(requestId);
        if (cancelled) return;
        setRequestDetail(r);

        const s = r?.status;
        if (s === "FAILED" || s === "EXPIRED" || s === "FULFILLED") {
          stopPoll();
        }
      } catch (e) {
        // Transient failures happen; keep polling but surface a hint.
        if (!cancelled) setPollError("Having trouble fetching status… retrying.");
      }
    };

    stopPoll();
    tick();
    pollTimerRef.current = setInterval(tick, 10000);

    return () => {
      cancelled = true;
      stopPoll();
    };
  }, [requestId]);

  const onSubmit = async () => {
    setSubmitError("");
    setPollError("");
    setRequestDetail(null);

    if (!domain) {
      setSubmitError("Brand is required.");
      return;
    }
    if (!startDate || !endDate) {
      setSubmitError("Start date and end date are required.");
      return;
    }
    if (startDate < MIN_EXPORT_START_DATE) {
      setSubmitError(`Start date must be on or after ${MIN_EXPORT_START_DATE}.`);
      return;
    }
    if (endDate < startDate) {
      setSubmitError("End date must be on or after start date.");
      return;
    }
    if (!isAsyncEligible) {
      setSubmitError("This range is ≤ 30 days. Use the table view instead of requesting an email export.");
      return;
    }
    if (!email.trim()) {
      setSubmitError("Email is required for ranges > 30 days.");
      return;
    }

    const payload = {
      domain,
      category: category || null,
      start_date: startDate,
      end_date: endDate,
      requester_email: email.trim(),
      criteria: {
        is_storewide: boolOrNull(isStorewide),
        is_significant: boolOrNull(isSignificant),
        is_steep: boolOrNull(isSteep),
        is_clearance: boolOrNull(isClearance),
        is_desperation: boolOrNull(isDesperation),
      },
    };

    setSubmitLoading(true);
    try {
      const url = getClientUrl("v1/export-requests/submit");
      const res = await axios.post(url, payload, getNumericalHeader());
      setRequestId(res.data?.id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to submit export request.";
      setSubmitError(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const status = requestDetail?.status || "";
  const downloadUrl = requestDetail?.download?.url || "";

  return (
    <div className="card" style={{ marginTop: "12px" }}>
      <div className="card-body">
        <h4 className="card-title">Request CSV Export (for ranges &gt; 30 days)</h4>

        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Start Date (min: {MIN_EXPORT_START_DATE})</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                min={MIN_EXPORT_START_DATE}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Email (required for &gt; 30 days)</Form.Label>
              <Form.Control
                type="email"
                value={email}
                disabled={!isAsyncEligible}
                placeholder="you@company.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Label>&nbsp;</Form.Label>
            <div>
              <Button
                variant="primary"
                disabled={submitLoading}
                onClick={onSubmit}
                style={{ width: "100%" }}
              >
                {submitLoading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Submitting…
                  </>
                ) : (
                  "Request CSV"
                )}
              </Button>
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: "10px" }}>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Storewide</Form.Label>
              <Form.Control as="select" value={isStorewide} onChange={(e) => setIsStorewide(e.target.value)}>
                <option value="none">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Significant</Form.Label>
              <Form.Control as="select" value={isSignificant} onChange={(e) => setIsSignificant(e.target.value)}>
                <option value="none">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Steep</Form.Label>
              <Form.Control as="select" value={isSteep} onChange={(e) => setIsSteep(e.target.value)}>
                <option value="none">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Clearance</Form.Label>
              <Form.Control as="select" value={isClearance} onChange={(e) => setIsClearance(e.target.value)}>
                <option value="none">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Desperation</Form.Label>
              <Form.Control as="select" value={isDesperation} onChange={(e) => setIsDesperation(e.target.value)}>
                <option value="none">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Alert variant="light" style={{ marginTop: "28px" }}>
              <div><b>Range:</b> {days === null ? "—" : `${days} days`}</div>
            </Alert>
          </Col>
        </Row>

        {submitError ? <Alert variant="danger">{submitError}</Alert> : null}
        {pollError ? <Alert variant="warning">{pollError}</Alert> : null}

        {requestId ? (
          <Alert variant="info" style={{ marginTop: "10px" }}>
            <div><b>Request:</b> #{requestId} {status ? `· ${status}` : ""}</div>
            {requestDetail?.not_before && (status === "PENDING" || status === "QUEUED") ? (
              <div className="text-muted">Earliest processing: {String(requestDetail.not_before)}</div>
            ) : null}
            {status === "FAILED" ? (
              <div style={{ marginTop: "6px" }}>
                <b>Failed:</b> {requestDetail?.error_message || "Unknown error"}
              </div>
            ) : null}
            {status === "FULFILLED" ? (
              <div style={{ marginTop: "6px" }}>
                {downloadUrl ? (
                  <a className="btn btn-success btn-sm" href={downloadUrl} target="_blank" rel="noreferrer">
                    Download CSV
                  </a>
                ) : (
                  <span className="text-muted">Download link is not available yet — refresh in a moment.</span>
                )}
              </div>
            ) : null}
          </Alert>
        ) : null}
      </div>
    </div>
  );
}


